import React, { useEffect, useMemo, useState, useContext } from "react";
import { LassieContext } from "../../../context/LassieProvider";
import { useParams, useNavigate } from "react-router-dom";
import { toPrettyTimeSince } from "../../../prettyDate";
import InfoCards from "../../../components/info-card/InfoCard";
import OlMap from "../../../components/ol-map/OlMap";
import TurbineAnimation from "../../../components/turbine-animation/TurbineAnimation";
import { useSyncSelectedWindfarm } from "../../../hooks/useSyncSelectedWindfarm";
import turbineManagerIcon from "../../../assets/img/turbine-manager-icon.png";
import turbineManagerIconColor from "../../../assets/img/turbine-manager-icon-color.png";
import "./lightning-windfarm-page.css";

const MAX_STRIKE_DISTANCE_KM = 2.5;

export default function LightningWindfarmPage() {
  const { windfarms, wfStatistics, fetchStatistics, fetchTurbines, turbines, fetchOrganizations } = useContext(LassieContext);
  const [showTurbineManager, setShowTurbineManager] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate()

  let { windfarm_id } = useParams();
  if (windfarm_id) {
    windfarm_id = parseInt(windfarm_id);
  }

  const windfarm = useMemo(() => {
    if (!windfarms || !windfarm_id) return null;
    return windfarms.find((wf) => wf.id === windfarm_id);
  }, [windfarms, windfarm_id]);

  const hasData = wfStatistics.length > 0 && turbines.length > 0;

  useEffect(() => {
    if (!windfarm_id) return;

    const fetchData = async () => {
      setIsLoading(true);
      await Promise.all([fetchStatistics(windfarm_id, MAX_STRIKE_DISTANCE_KM), fetchTurbines(windfarm_id, null, MAX_STRIKE_DISTANCE_KM)]);
      setIsLoading(false);
    };

    fetchData();
    fetchOrganizations();
  }, [windfarm_id]);

  useSyncSelectedWindfarm(windfarm_id);

  return (
    <div className="lightning-windfarm-page">
      {isLoading ? (
        <TurbineAnimation />
      ) : hasData ? (
        <>
          <div className="statistics-container">
            <div>
              <h1 style={{ marginBottom: 0 }}>{windfarm && windfarm.name}</h1>
              {windfarm && (
                <p className="latest-data">
                  Latest data acquisition:{" "}
                  {toPrettyTimeSince(new Date(windfarm.latest_data))}
                </p>
              )}
              <p className="distance-note">
                Showing lightning strikes within {MAX_STRIKE_DISTANCE_KM} km of a turbine
              </p>
            </div>

            <div className="lightning-activity-wrapper">
              <InfoCards statistics={wfStatistics} windfarmPage={true} />
            </div>
            <div className="turbine-manager-wrapper">
              <button
                className="turbine-manager-btn"
                value="Turbine manager"
                onClick={_ => {
                  window.open(`/lassie/windfarm/${windfarm_id}/turbine-manager`, '_blank');
                }}
              >
                Turbine Manager
                <img
                  src={turbineManagerIcon}
                  className="turbine-manager-icon default-icon"
                  alt='Turbine icon'
                />
                <img
                  src={turbineManagerIconColor}
                  className="turbine-manager-icon hover-icon"
                  alt='Turbine icon'
                />
              </button>
            </div>
          </div>

          <div className="map-wrapper">
            <OlMap
              key="main-map"
              lightning_strikes={[
                ...turbines.map((t) => t.turbine_lightning),
              ].flat()}
              turbines={turbines}
            />
          </div>
        </>
      ) : (
        <p>No data available for this windfarm.</p>
      )}
    </div>
  );
}
